/**
 * Infinite Scroll - Professional Implementation
 * Based on Shopify Dawn theme best practices
 */

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Get the product grid container
    this.container = document.querySelector('[data-product-grid]');
    this.nextPageUrl = this.getAttribute('data-next-url');
    this.isLoading = false;

    if (!this.container || !this.nextPageUrl || this.nextPageUrl === 'null' || this.nextPageUrl === '') {
      return;
    }

    // Create and setup
    this.createLoadingIndicator();
    this.setupIntersectionObserver();
  }

  createLoadingIndicator() {
    this.loadingIndicator = document.createElement('div');
    this.loadingIndicator.className = 'infinite-scroll-loading';
    this.loadingIndicator.innerHTML = `
      <div style="text-align: center; padding: 40px;">
        <div class="spinner" style="
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #333;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        "></div>
        <p style="color: #666; font-size: 14px;">Loading more products...</p>
      </div>
    `;
    this.loadingIndicator.style.display = 'none';
    
    // Insert after the grid
    if (this.container.parentElement) {
      this.container.parentElement.insertBefore(this.loadingIndicator, this.container.nextSibling);
    }
    
    // Add CSS animation
    if (!document.getElementById('infinite-scroll-spinner-css')) {
      const style = document.createElement('style');
      style.id = 'infinite-scroll-spinner-css';
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  setupIntersectionObserver() {
    // Use THIS element as the sentinel (the <infinite-scroll> tag itself)
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading && this.nextPageUrl) {
          this.loadMore();
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px 600px 0px', // Trigger 600px before element comes into view
      threshold: 0
    });

    observer.observe(this);
  }

  async loadMore() {
    if (this.isLoading || !this.nextPageUrl) {
      return;
    }

    this.isLoading = true;
    
    // Show loading
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'block';
    }

    try {
      const response = await fetch(this.nextPageUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find new products
      const newGrid = doc.querySelector('[data-product-grid]');
      
      if (!newGrid) {
        throw new Error('Product grid not found');
      }

      const newProducts = Array.from(newGrid.children);

      // Hide sold out products if setting is enabled
      const hideSoldOut = window.Shopify?.theme?.settings?.hide_sold_out_products ?? false;

      // Append new products (filter out sold-out if setting enabled)
      newProducts.forEach((product) => {
        if (hideSoldOut && this.isProductOutOfStock(product)) {
          return; // Skip sold-out products
        }
        this.container.appendChild(product);
      });

      // Re-sort all products if setting is enabled (and not hiding sold-out)
      if (!hideSoldOut) {
        this.sortProductsByAvailability();
      }

      // Update next page URL
      const newInfiniteScroll = doc.querySelector('infinite-scroll');
      if (newInfiniteScroll) {
        const newNextUrl = newInfiniteScroll.getAttribute('data-next-url');
        
        if (newNextUrl && newNextUrl !== 'null' && newNextUrl !== '') {
          this.nextPageUrl = newNextUrl;
        } else {
          this.nextPageUrl = null;
          this.showEndMessage();
        }
      } else {
        this.nextPageUrl = null;
        this.showEndMessage();
      }

      // Update URL in browser (optional) - preserve scroll position
      if (this.nextPageUrl && window.history && window.history.replaceState) {
        try {
          // Save current scroll position
          const scrollY = window.scrollY || window.pageYOffset;
          
          // Handle both relative and absolute URLs
          const url = this.nextPageUrl.startsWith('http') 
            ? this.nextPageUrl 
            : window.location.origin + this.nextPageUrl;
          const urlObj = new URL(url);
          
          // Update URL with scroll position saved in state
          window.history.replaceState({ scrollY: scrollY }, '', urlObj.pathname + urlObj.search);
          
          // Restore scroll position immediately
          window.scrollTo(0, scrollY);
        } catch (e) {
          // Silently fail if URL update doesn't work
        }
      }
      
    } catch (error) {
      this.showError(error);
    } finally {
      this.isLoading = false;
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'none';
      }
    }
  }

  showEndMessage() {
    if (this.loadingIndicator) {
      this.loadingIndicator.innerHTML = `
        <div style="text-align: center; padding: 30px; color: #999; font-size: 14px;">
          You've reached the end of the collection
        </div>
      `;
      this.loadingIndicator.style.display = 'block';
      setTimeout(() => {
        this.loadingIndicator.style.display = 'none';
      }, 3000);
    }
  }

  showError(error) {
    if (this.loadingIndicator) {
      this.loadingIndicator.innerHTML = `
        <div style="text-align: center; padding: 30px; color: #d32f2f;">
          <p>Failed to load more products</p>
          <p style="font-size: 12px; color: #999; margin-top: 10px;">${error.message}</p>
        </div>
      `;
      this.loadingIndicator.style.display = 'block';
    }
  }

  sortProductsByAvailability() {
    // Check if sorting is enabled in theme settings
    const sortingEnabled = window.Shopify?.theme?.settings?.move_out_of_stock_to_end ?? true;
    
    if (!sortingEnabled) {
      return;
    }

    // Get all product items
    const allProducts = Array.from(this.container.children);
    
    if (allProducts.length === 0) {
      return;
    }

    // Separate in-stock and out-of-stock products
    const inStockProducts = [];
    const outOfStockProducts = [];

    allProducts.forEach((product) => {
      const isOutOfStock = this.isProductOutOfStock(product);
      
      if (isOutOfStock) {
        outOfStockProducts.push(product);
      } else {
        inStockProducts.push(product);
      }
    });

    // Clear container
    this.container.innerHTML = '';

    // Append in-stock first, then out-of-stock
    inStockProducts.forEach(product => this.container.appendChild(product));
    outOfStockProducts.forEach(product => this.container.appendChild(product));
  }

  isProductOutOfStock(productElement) {
    // Method 1: Check for sold-out badge
    if (productElement.querySelector('.badge--sold-out, .badge--soldout')) {
      return true;
    }

    // Method 2: Check for disabled add to cart button
    const addButton = productElement.querySelector('button[name="add"], input[name="add"]');
    if (addButton && addButton.disabled) {
      return true;
    }

    // Method 3: Check for sold-out class on product card
    if (productElement.classList.contains('product-card--sold-out') || 
        productElement.classList.contains('card--soldout')) {
      return true;
    }

    // Method 4: Check product form availability
    const productForm = productElement.querySelector('product-form, [data-product-form]');
    if (productForm) {
      const availableInput = productForm.querySelector('input[name="available"]');
      if (availableInput && availableInput.value === 'false') {
        return true;
      }
    }

    // Method 5: Check for "sold out" text in button
    const buttons = productElement.querySelectorAll('button');
    for (const button of buttons) {
      const text = button.textContent.toLowerCase();
      if (text.includes('sold out') || text.includes('tükendi')) {
        return true;
      }
    }

    return false;
  }
}

customElements.define('infinite-scroll', InfiniteScroll);
